import { SvgIcon } from '@progress/kendo-react-common'
import { clockIcon, paperPlaneIcon } from '@progress/kendo-svg-icons'
import { useState, useRef, PropsWithChildren } from 'react'
import { fetchApi } from 'services/api'
import { HoverCard } from './HoverCard'

type PersonInfo = {
  DisplayName: string;
  Mail: string;
  MobilePhone: string;
  JobTitle: string;
  Location: string;
  Avatar: string;
  ManagerUserId?: number;
  ManagerName: string;
  Schedule?: {
    Timezone: string
    StartTime: string
    EndTime: string
    LocationType: string
    IsAvailable: boolean
  }
}

type PersonHoverCardProps = {
  userId: number;
};

const fetchPersonData = async (userId: number): Promise<PersonInfo | null> => {
  return await fetchApi(`/api/User/PersonHoverCard/${userId}`)
    .catch((): null => {
      return null;
    });
}

export const PersonHoverCard: React.FC<PropsWithChildren<PersonHoverCardProps>> = (props) =>
  <PersonHoverCardContent key={props.userId} {...props} />

const PersonHoverCardContent: React.FC<PropsWithChildren<PersonHoverCardProps>> = ({ userId, children }) => {
  const [person, setPerson] = useState<PersonInfo | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const request = useRef(0)

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatWorkingHours = () => {
    if (!person?.Schedule) return null

    const { StartTime, EndTime, Timezone, IsAvailable, LocationType } = person.Schedule
    return {
      hours: `${StartTime} - ${EndTime}`,
      timezone: Timezone,
      isAvailable: IsAvailable,
      locationType: LocationType
    }
  }

  const load = async () => {
    if (person || isLoading) return
    const currentRequest = ++request.current
    setIsLoading(true)
    try {
      const data = await fetchPersonData(userId)
      if (request.current === currentRequest) setPerson(data)
    } finally {
      if (request.current === currentRequest) setIsLoading(false)
    }
  }

  const workingInfo = formatWorkingHours()

  return <HoverCard key={userId} label="Person details" width={320} onOpen={load} content={<>
              {isLoading && !person ? (
                <div className="d-flex align-items-center justify-content-center" style={{ height: '120px' }}>
                  <div className="spinner-border spinner-border-sm text-primary mr-2" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="text-muted">Loading...</span>
                </div>
              ) : person ? (
                <>
                  <div className="d-flex align-items-start mb-3">
                    <div className="position-relative mr-3">
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: '64px',
                          height: '64px',
                          backgroundColor: person.Avatar ? 'transparent' : '#d1ecde',
                          backgroundImage: person.Avatar ? `url(${person.Avatar})` : 'none',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          border: '2px solid rgba(100, 149, 237, 0.2)',
                          fontSize: 26,
                          fontWeight: '600',
                          color: '#1b5e3c'
                        }}
                      >
                        {!person.Avatar && getInitials(person.DisplayName)}
                      </div>
                    </div>

                    <div className="flex-grow-1">
                      <h6 className="card-title mb-1 font-weight-bold">
                        {person.DisplayName}
                      </h6>
                      <p className="card-text mb-0 k-text-subtle small">
                        {person.JobTitle}
                      </p>
                      {person.ManagerName && person.ManagerUserId && person.ManagerUserId !== userId ? (
                        <p className="card-text mb-0 k-text-subtle small">
                          Reports to{' '}
                          <PersonHoverCard userId={person.ManagerUserId}>
                            <span style={{ cursor: 'pointer', textDecoration: 'underline' }}>{person.ManagerName}</span>
                          </PersonHoverCard>
                        </p>
                      ) : person.ManagerName ? (
                        <p className="card-text mb-0 k-text-subtle small">
                          Reports to {person.ManagerName}
                        </p>
                      ) : null}
                      {person.Location && <p className="card-text mb-0 k-text-subtle small">
                        {person.Location}
                      </p>}
                    </div>
                  </div>

                  {person.Mail && <div className="d-flex align-items-center mb-2">
                    <SvgIcon themeColor='primary' icon={paperPlaneIcon} className='mr-2' />
                    <a href={`mailto:${person.Mail}`} className="small font-weight-medium">{person.Mail}</a>
                  </div>}

                  {workingInfo && <>
                    <div className="d-flex align-items-center mb-2">
                      <SvgIcon themeColor='primary' icon={clockIcon} className='mr-2' />
                      <span className="small k-text-subtle">
                        {workingInfo.hours} {workingInfo.timezone}
                      </span>
                    </div>

                    <span
                      className={`badge badge-pill px-2 py-1 small ${
                        workingInfo.isAvailable 
                          ? "text-success" 
                          : "text-secondary"
                      }`}
                      style={{
                        backgroundColor: workingInfo.isAvailable ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                        border: `1px solid ${workingInfo.isAvailable ? 'rgba(76, 175, 80, 0.2)' : 'rgba(158, 158, 158, 0.2)'}`
                      }}
                    >
                      {workingInfo.isAvailable
                        ? workingInfo.locationType === "Remote"
                          ? "Currently working remotely"
                          : workingInfo.locationType === "Office"
                          ? "Currently working on-site"
                          : "Currently working"
                        : "Outside working hours"}
                    </span>
                  </>}
                </>
              ) : (
                <div className="d-flex align-items-center justify-content-center" style={{ height: '120px' }}>
                  <span className="k-text-subtle">Person not found</span>
                </div>
              )}
  </>}>{children}</HoverCard>
}
